import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { adminGetIntelContent } from '../../../api/Api';
import { useColors } from '../../../theme/ColorModeContext';
import type { ChallengeContentRow, IntelContent as Content } from './types';
import { TYPE_LABELS, fmtHours, fmtMinutes, fmtNumber, fmtPct } from './format';
import { MONO, Section, TrackingNote, useTableSx } from './widgets';
import { SortHeader, sortRows, useSort } from './table';

interface Props {
    token: string;
    includeAdmins: boolean;
}

const IntelContent = ({ token, includeAdmins }: Props) => {
    const colors = useColors();
    const table = useTableSx();
    const [data, setData] = useState<Content | null>(null);
    const [loading, setLoading] = useState(true);
    const [track, setTrack] = useState('all');
    const sort = useSort<ChallengeContentRow>('openers');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        adminGetIntelContent(token, includeAdmins).then((result) => {
            if (cancelled) return;
            setData(result);
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [token, includeAdmins]);

    const tracks = useMemo(() => {
        if (!data) return [];
        const present = new Set([...data.challenges.map((row) => row.track), ...data.stages.map((row) => row.track)]);
        return Array.from(present).sort();
    }, [data]);

    if (loading && !data) {
        return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: colors.accent.cyan }} /></Box>;
    }
    if (!data) {
        return <Typography sx={{ color: colors.text.muted }}>Could not load content intel.</Typography>;
    }

    const label = (key: string) => data.track_labels[key] || key;
    const challenges = sortRows(
        data.challenges.filter((row) => track === 'all' || row.track === track),
        sort.key,
        sort.dir,
    );
    const stages = data.stages.filter((row) => track === 'all' || row.track === track);

    const trackFilter = (
        <Select size="small" value={track} onChange={(event) => setTrack(String(event.target.value))} sx={{ fontFamily: MONO, fontSize: '0.78rem', minWidth: 180 }}>
            <MenuItem value="all">All tracks</MenuItem>
            {tracks.map((key) => <MenuItem key={key} value={key}>{label(key)}</MenuItem>)}
        </Select>
    );

    return (
        <Box sx={{ opacity: loading ? 0.6 : 1 }}>
            <TrackingNote since={data.tracking_since} />

            <Section
                title="Challenges"
                action={trackFilter}
                caption={
                    <>
                        <b>Opened</b> counts anyone who opened or solved it; <b>attempted</b> anyone who compiled, tested or solved.
                        Solve rate is solved ÷ attempted. <b>Stuck</b>: unsolved with 3+ failed test runs. <b>Abandoned</b>: unsolved and
                        untouched for 7+ days. Time to solve is wall-clock from first opening it, so a weekend break counts. Sort by
                        solve rate or stuck users to find the challenges that lose people.
                    </>
                }
            >
                <TableContainer sx={{ overflowX: 'auto', maxHeight: 640 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={table.head}>
                                <SortHeader label="Challenge" field="name" sort={sort} align="left" />
                                <SortHeader label="Diff" field="difficulty" sort={sort} />
                                <SortHeader label="Opened" field="openers" sort={sort} />
                                <SortHeader label="Attempted" field="attempters" sort={sort} />
                                <SortHeader label="Solved" field="solvers" sort={sort} />
                                <SortHeader label="Solve rate" field="solve_rate" sort={sort} />
                                <SortHeader label="Tests to solve" field="median_tests_to_solve" sort={sort} title="Median test runs, including the passing one" />
                                <SortHeader label="Time to solve" field="median_hours_to_solve" sort={sort} title="Median, first open to solve" />
                                <SortHeader label="Failed tests" field="failed_tests" sort={sort} />
                                <SortHeader label="Stuck" field="stuck_users" sort={sort} />
                                <SortHeader label="Abandoned" field="abandoned_users" sort={sort} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {challenges.map((row) => (
                                <TableRow key={row.id} sx={{ ...table.row, opacity: row.active ? 1 : 0.55 }}>
                                    <TableCell>
                                        <Typography sx={{ fontSize: '0.82rem', color: colors.text.primary }}>
                                            {row.name}{!row.active && ' (inactive)'}
                                        </Typography>
                                        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: colors.text.muted }}>
                                            {label(row.track)} · {TYPE_LABELS[row.type] || row.type} · {row.category}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.difficulty}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtNumber(row.openers)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtNumber(row.attempters)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtNumber(row.solvers)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtPct(row.solve_rate)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.median_tests_to_solve ?? '-'}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtHours(row.median_hours_to_solve)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtNumber(row.failed_tests)}</TableCell>
                                    <TableCell align="right" sx={{ ...table.mono, color: row.stuck_users ? colors.accent.red : table.mono.color }}>{row.stuck_users}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.abandoned_users}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            <Section
                title="Theory stages"
                caption={
                    <>
                        In lock order within each category. <b>From previous</b>: of the users who completed the previous stage, the share
                        who opened this one; a sharp drop marks where a track loses readers. <b>Wrong answers</b> is the share of submitted
                        answers that were wrong, which flags confusing questions or reading that does not prepare for them.
                    </>
                }
            >
                <TableContainer sx={{ overflowX: 'auto', maxHeight: 640 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Stage</TableCell>
                                <TableCell align="right">Opened</TableCell>
                                <TableCell align="right">Completed</TableCell>
                                <TableCell align="right">Completion</TableCell>
                                <TableCell align="right">From previous</TableCell>
                                <TableCell align="right">Wrong answers</TableCell>
                                <TableCell align="right" title="Median, first open to completion">Time to complete</TableCell>
                                <TableCell align="right">Abandoned</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stages.map((row, index) => {
                                const newCategory = index === 0 || stages[index - 1].category !== row.category;
                                return (
                                    <React.Fragment key={row.id}>
                                        {newCategory && (
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ fontFamily: MONO, fontSize: '0.68rem', letterSpacing: '0.1em', color: colors.accent.cyan, borderBottom: `1px solid ${colors.border.default}`, pt: 2 }}>
                                                    {label(row.track).toUpperCase()} · {row.category}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow sx={{ ...table.row, opacity: row.active ? 1 : 0.55 }}>
                                            <TableCell>{row.order_index}. {row.title}{!row.active && ' (inactive)'}</TableCell>
                                            <TableCell align="right" sx={table.mono}>{row.viewers}</TableCell>
                                            <TableCell align="right" sx={table.mono}>{row.solvers}</TableCell>
                                            <TableCell align="right" sx={table.mono}>{fmtPct(row.completion_rate)}</TableCell>
                                            <TableCell align="right" sx={table.mono}>{fmtPct(row.continuation_rate)}</TableCell>
                                            <TableCell align="right" sx={table.mono}>{fmtPct(row.wrong_answer_rate)} <Box component="span" sx={{ color: colors.text.muted }}>({row.wrong_answers}/{row.answers})</Box></TableCell>
                                            <TableCell align="right" sx={table.mono}>{fmtMinutes(row.median_minutes_to_complete)}</TableCell>
                                            <TableCell align="right" sx={table.mono}>{row.abandoned_users}</TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            <Section title="Public tech quizzes" caption="All attempts including anonymous visitors. Signed-in takers are distinct accounts that took the quiz while logged in.">
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Quiz</TableCell>
                                <TableCell align="right">Started</TableCell>
                                <TableCell align="right">Finished</TableCell>
                                <TableCell align="right">Completion</TableCell>
                                <TableCell align="right">Avg score</TableCell>
                                <TableCell align="right">Avg time</TableCell>
                                <TableCell align="right">Signed-in takers</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.quizzes.map((row) => (
                                <TableRow key={row.slug} sx={table.row}>
                                    <TableCell>{row.title}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.started}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.completed}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtPct(row.completion_rate)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.avg_score != null ? `${row.avg_score.toFixed(1)} / 30` : '-'}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.avg_duration_seconds != null ? fmtMinutes(row.avg_duration_seconds / 60) : '-'}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.signed_in_takers}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>
        </Box>
    );
};

export default IntelContent;
